import dayjs from "dayjs";
import {
    Box,
    Button,
    Chip,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {memo, useMemo, useState} from "react";
import Audience from "../Publisher/Audience";

export const normalizeHistoryData = (value) => {
    if (value == null) return {};
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
};

export const getEffectiveCampaignRows = (info = {}) => {
    const before = info.effectiveCampaignsBefore || {};
    const after = info.effectiveCampaignsAfter || {};
    const cohorts = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();

    return cohorts.map(cohort => {
        const beforeCampaigns = before[cohort] || [];
        const afterCampaigns = after[cohort] || [];

        return {
            cohort,
            before: beforeCampaigns,
            after: afterCampaigns,
            added: afterCampaigns.filter(campaign => !beforeCampaigns.includes(campaign)),
            removed: beforeCampaigns.filter(campaign => !afterCampaigns.includes(campaign))
        };
    });
};

export const hasAudienceChanges = (oldData = {}, newData = {}) => (
    Array.isArray(newData.audiences)
    && JSON.stringify(oldData.audiences || []) !== JSON.stringify(newData.audiences)
);

export const hasEffectiveCampaigns = (rows = []) => rows.some(
    row => row.before.length > 0 || row.after.length > 0
);

const normalizeCohorts = (cohorts = {}) => {
    if (Array.isArray(cohorts)) {
        return Object.fromEntries(cohorts.map(cohort => [cohort._id || cohort.id, cohort]));
    }

    return cohorts || {};
};

const normalizeValue = value => {
    if (Array.isArray(value)) return value.map(normalizeValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.keys(value).sort().map(key => [key, normalizeValue(value[key])]));
    }

    return value;
};

const getComparableCohort = cohort => normalizeValue({
    active: cohort.active,
    limit: cohort.limit ?? null,
    weight: cohort.weight,
    parameters: cohort.parameters || {},
    userGroups: [...(cohort.userGroups || [])].sort()
});

export const getCohortChanges = (oldCohorts = {}, newCohorts = {}) => {
    const before = normalizeCohorts(oldCohorts);
    const after = normalizeCohorts(newCohorts);
    const cohortIds = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();

    return cohortIds.reduce((changes, cohortId) => {
        if (!before[cohortId]) {
            changes.push({id: cohortId, type: 'added', before: null, after: after[cohortId]});
        } else if (!after[cohortId]) {
            changes.push({id: cohortId, type: 'removed', before: before[cohortId], after: null});
        } else if (JSON.stringify(getComparableCohort(before[cohortId])) !== JSON.stringify(getComparableCohort(after[cohortId]))) {
            changes.push({id: cohortId, type: 'changed', before: before[cohortId], after: after[cohortId]});
        }

        return changes;
    }, []);
};

const ACTION_NAMES = {
    STATUS_CHANGING: 'changed status',
    EVENT_CREATION: 'created A/B test',
    EVENT_UPDATE: 'edited A/B test',
    EVENT_CREATION_BY_TRANSFER: 'created A/B test by transfer',
    EVENT_UPDATE_BY_TRANSFER: 'edited A/B test by transfer'
};

const CampaignList = ({campaigns, env, emptyText = 'None'}) => campaigns.length > 0 ? (
    <Box className="abtest-history-campaigns">
        {campaigns.map(campaign => (
            <Chip
                key={campaign}
                component={Link}
                clickable
                href={`/creator/campaigns/${campaign}?env=${env}`}
                target="_blank"
                size="small"
                label={campaign}
                variant="outlined"
            />
        ))}
    </Box>
) : <Typography component="span" variant="body2" color="text.secondary">{emptyText}</Typography>;

const ChangeList = ({row}) => {
    if (row.added.length === 0 && row.removed.length === 0) {
        return <Typography component="span" variant="body2" color="text.secondary">No changes</Typography>;
    }

    return <Box className="abtest-history-changes">
        {row.added.map(campaign => <Chip key={`added-${campaign}`} size="small" color="success" label={`+ ${campaign}`}/>)}
        {row.removed.map(campaign => <Chip key={`removed-${campaign}`} size="small" color="error" label={`− ${campaign}`}/>)}
    </Box>;
};

const formatParameter = value => typeof value === 'string' ? value : JSON.stringify(value);

const CohortState = ({cohort}) => {
    if (!cohort) return <Typography variant="body2" color="text.secondary">—</Typography>;

    const parameters = Object.entries(cohort.parameters || {});
    const userGroups = cohort.userGroups || [];

    return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
        <Typography variant="body2">Active: <b>{cohort.active ? 'Yes' : 'No'}</b></Typography>
        <Typography variant="body2">Weight: <b>{cohort.weight ?? '—'}</b></Typography>
        <Typography variant="body2">Limit: <b>{cohort.limit ?? 'No limit'}</b></Typography>
        {userGroups.length > 0 && <Typography variant="body2">
            User groups: <b>{userGroups.join(', ')}</b>
        </Typography>}
        {parameters.length > 0 && <Typography variant="body2">
            Parameters: {parameters.map(([key, value]) => `${key}: ${formatParameter(value)}`).join(', ')}
        </Typography>}
    </Box>;
};

const getStatus = data => normalizeHistoryData(data).status;

const AbTestHistoryItem = memo(function({item, env, setSelectedRow, setOpenDiff, setOpenInfo}) {
    const [showDetails, setShowDetails] = useState(false);
    const info = normalizeHistoryData(item.info);
    const rows = useMemo(() => getEffectiveCampaignRows(info), [info]);
    const oldData = normalizeHistoryData(item.oldData);
    const newData = normalizeHistoryData(item.newData);
    const isCampaignChange = item.eventType === 'CAMPAIGN';
    const isStatusChange = item.adminAction === 'STATUS_CHANGING';
    const audienceChanged = item.eventType === 'AB_TEST' && hasAudienceChanges(oldData, newData);
    const showEffectiveCampaigns = hasEffectiveCampaigns(rows);
    const cohortChanges = useMemo(() => getCohortChanges(oldData.cohorts, newData.cohorts), [oldData.cohorts, newData.cohorts]);

    const openJson = () => {
        setSelectedRow({oldData, newData, info});
        setOpenInfo(false);
        setOpenDiff(true);
    };

    const openHistoryInfo = () => {
        setSelectedRow({oldData, newData, info});
        setOpenInfo(true);
        setOpenDiff(true);
    };

    return <div className={`history-item-group ${showDetails ? 'expanded' : ''}`}>
        <div className={`history-item ${showDetails ? 'expanded' : ''}`}>
            <div className="history-item__date">
                {dayjs(item.creationDateSeconds * 1000).utcOffset(180).format('YYYY-MM-DD HH:mm:ss')}
            </div>
            <div className={`history-owner ${(item.userName || '').toLowerCase()}`}>{item.userName}</div>
            <div>
                {isCampaignChange ? <>
                    changed effective campaigns via <b>{item.eventId}</b>
                </> : <>
                    {ACTION_NAMES[item.adminAction] || item.adminAction}
                    {isStatusChange && <>
                        {' from '}<span className={`status status-${(getStatus(item.oldData) || '').toLowerCase()}`}>{getStatus(item.oldData) || 'n/a'}</span>
                        {' to '}<span className={`status status-${(getStatus(item.newData) || '').toLowerCase()}`}>{getStatus(item.newData) || 'n/a'}</span>
                        {info.newWinningCohortId && <>; winning cohort <b>{info.newWinningCohortId}</b></>}
                    </>}
                </>}
            </div>
            <div className="history-details">
                <Button size="small" variant="outlined" onClick={() => setShowDetails(!showDetails)}>
                    {showDetails ? 'Close' : 'Details'}
                </Button>
            </div>
        </div>

        {showDetails && <div className="history-item abtest-history-details">
            {audienceChanged && <Table size="small" className="abtest-history-abtest-changes">
                <TableBody>
                    <TableRow>
                        <TableCell sx={{width: 134}}><b>Audience:</b></TableCell>
                        <TableCell>
                            {newData.audiences.length > 0 ? newData.audiences.map((audience, index) => (
                                <Audience key={index} audience={audience}>
                                    Audience {(index + 1)}<br/>
                                </Audience>
                            )) : <Typography variant="body2" color="text.secondary">None</Typography>}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>}

            {cohortChanges.length > 0 && <>
                <Typography variant="subtitle2" sx={{mb: 1}}>Cohort changes</Typography>
                <Table size="small" sx={{mb: 2}}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Cohort</TableCell>
                            <TableCell>Change</TableCell>
                            <TableCell>Before</TableCell>
                            <TableCell>After</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cohortChanges.map(change => <TableRow key={change.id}>
                            <TableCell><b>{change.id}</b></TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    color={{added: 'success', removed: 'error', changed: 'warning'}[change.type]}
                                    label={change.type[0].toUpperCase() + change.type.slice(1)}
                                />
                            </TableCell>
                            <TableCell><CohortState cohort={change.before}/></TableCell>
                            <TableCell><CohortState cohort={change.after}/></TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </>}

            {showEffectiveCampaigns && <>
                <Typography variant="subtitle2" sx={{mb: 1}}>Effective campaigns by cohort</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Cohort</TableCell>
                            <TableCell>Before</TableCell>
                            <TableCell>Change</TableCell>
                            <TableCell>After</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => <TableRow key={row.cohort}>
                            <TableCell><b>{row.cohort}</b></TableCell>
                            <TableCell><CampaignList campaigns={row.before} env={env}/></TableCell>
                            <TableCell><ChangeList row={row}/></TableCell>
                            <TableCell><CampaignList campaigns={row.after} env={env}/></TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </>}

            {isStatusChange && <Box sx={{mt: 1}}>
                Winning cohort: <b>{info.oldWinningCohortId || 'None'}</b> → <b>{info.newWinningCohortId || 'None'}</b>
            </Box>}

            <Box className="abtest-history-actions">
                <Button size="small" variant="outlined" onClick={openJson}>JSON diff</Button>
                <Button size="small" variant="outlined" onClick={openHistoryInfo}>Show info</Button>
            </Box>
        </div>}
    </div>;
});

export default AbTestHistoryItem;
